import { Button, Modal, IconButton, Divider, Skeleton } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import CloseIcon from "@mui/icons-material/Close"
import { ReactComponent as Download } from "components/icons/svgs/download.svg"
import { useLazyGetPaySlipListQuery } from "shared/redux/endpoints/employee"

import {
  containerStyle,
  headerStyle,
  nameStyle,
  roleStyle,
} from "pages/my-self/styles/ProfileHeader"

import "./ModalLeaveApplication.scss"

const PayslipSkeletonContent = () => (
  <MDBox className="payslip-skeleton-content">
    <MDBox className="payslip-loading-overlay">
      <div className="payslip-loading-spinner" />
      <MDTypography className="payslip-loading-message">
        Preparing your payslip...
      </MDTypography>
      <MDTypography className="payslip-loading-submessage">
        This may take a few seconds
      </MDTypography>
    </MDBox>

    <MDBox className="payslip-skeleton-section">
      <Skeleton
        variant="rounded"
        width={120}
        height={22}
        className="payslip-skeleton-section-title"
      />
      {Array.from({ length: 4 }).map((_, index) => (
        <MDBox key={`earning-skeleton-${index}`} className="payslip-skeleton-row">
          <Skeleton variant="text" width={`${55 - index * 5}%`} height={20} />
          <Skeleton variant="text" width={`${20 + index * 3}%`} height={20} />
        </MDBox>
      ))}
    </MDBox>

    <Divider sx={{ my: 2 }} />

    <MDBox className="payslip-skeleton-section">
      <Skeleton
        variant="rounded"
        width={130}
        height={22}
        className="payslip-skeleton-section-title"
      />
      {Array.from({ length: 3 }).map((_, index) => (
        <MDBox
          key={`deduction-skeleton-${index}`}
          className="payslip-skeleton-row"
        >
          <Skeleton variant="text" width={`${50 - index * 4}%`} height={20} />
          <Skeleton variant="text" width={`${18 + index * 2}%`} height={20} />
        </MDBox>
      ))}
    </MDBox>

    <MDBox className="payslip-skeleton-net">
      <Skeleton variant="rounded" width={90} height={24} />
      <Skeleton variant="rounded" width={140} height={28} />
    </MDBox>
  </MDBox>
)

const ModalPaySlipDetail = ({
  isOpen,
  onClose = () => {},
  data = {},
  openSnackbar,
  setOpenSnackbar,
}) => {
  const contentRef = useRef(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [getListPaySlip, { isFetching }] = useLazyGetPaySlipListQuery()

  const [payslipDetail, setPayslipDetail] = useState(null)

  const parseNumber = (value) => {
    if (value === null || value === undefined) return null
    const cleaned = String(value).replace(/,/g, "").trim()
    if (cleaned === "") return null
    if (!/^-?\d+(\.\d+)?$/.test(cleaned)) return null
    return Number(cleaned)
  }

  const toKeyValueMap = (list = []) =>
    list.reduce((acc, item) => {
      if (item?.Key) acc[item.Key] = item
      return acc
    }, {})

  const mapItems = (list = []) =>
    list.map((item) => {
      const amount = parseNumber(item?.Value)
      return {
        title: item?.Text ?? item?.Key ?? "-",
        rawValue: item?.Value ?? "",
        amount,
        isNumber: amount !== null,
      }
    })

  const formatVND = (value) => {
    const num = Number(value || 0)
    return new Intl.NumberFormat("vi-VN").format(num) + " VND"
  }

  const monthLabel = useMemo(() => {
    if (data?.month && data?.year) {
      if (typeof data.month === "number") {
        const monthName = new Date(data.year, data.month - 1, 1).toLocaleString(
          "en-US",
          { month: "long" }
        )
        return `${monthName} ${data.year}`
      }
      return `${data.month} ${data.year}`
    }
    return "-"
  }, [data?.month, data?.year])

  const uiModel = useMemo(() => {
    if (!payslipDetail) return null

    const employeeInfo = toKeyValueMap(payslipDetail?.employee_info || [])

    const earningsSections = [
      { title: "Salary Expat", items: mapItems(payslipDetail?.salary_expat) },
      {
        title: "Salary Allowance Expat",
        items: mapItems(payslipDetail?.salary_allowance_expat),
      },
      {
        title: "Additional Payment Expat",
        items: mapItems(payslipDetail?.additional_payment_expat),
      },
      {
        title: "Total Income Before Tax Expat",
        items: mapItems(payslipDetail?.total_income_before_tax_expat),
      },
      {
        title: "Salary Allowance Local",
        items: mapItems(payslipDetail?.salary_allowance_local),
      },
      { title: "Salary Local", items: mapItems(payslipDetail?.salary_local) },
    ].filter((section) => section.items.length > 0)

    const deductionsSections = [
      {
        title: "Salary Deduction Expat",
        items: mapItems(payslipDetail?.salary_deduction_expat),
      },
      {
        title: "Salary Deduction Local",
        items: mapItems(payslipDetail?.salary_deduction_local),
      },
    ].filter((section) => section.items.length > 0)

    const netIncomeItem =
      (payslipDetail?.salary_expat || []).find(
        (x) => x?.Key === "TT_NetIncome"
      ) ||
      (payslipDetail?.salary_local || []).find((x) => x?.Key === "TH_NetInCome")

    const netIncome = netIncomeItem?.Value ?? null

    return {
      employeeName:
        employeeInfo?.EmpName?.Value || payslipDetail?.employeeName || "-",
      departmentName:
        employeeInfo?.TK_ChucDanh?.Value ||
        employeeInfo?.TK_Centre_Backoffice?.Value ||
        payslipDetail?.departmentName ||
        "-",
      empCode: employeeInfo?.EmpCode?.Value || "-",
      avatarUrl: payslipDetail?.avatarUrl,
      earningsSections,
      deductionsSections,
      netIncome,
    }
  }, [payslipDetail])

  const netPay = useMemo(() => {
    const net = parseNumber(uiModel?.netIncome)
    if (net !== null) return net
    return 0
  }, [uiModel])

  const isLoading = isFetching && !uiModel

  useEffect(() => {
    if (!isOpen) return
    if (!data?.empCode || !data?.currentMonth) return
    ;(async () => {
      try {
        const res = await getListPaySlip({
          EmployeeCode: data.empCode,
          Month: data.currentMonth,
          LanguageCode: "VN",
        }).unwrap()

        const raw = res?.Data || res?.data?.Data || res?.data || res
        const picked = Array.isArray(raw)
          ? raw.find(
              (x) =>
                String(x.month) === String(data.month) &&
                String(x.year) === String(data.year)
            ) || raw[0]
          : raw?.Data || raw

        setPayslipDetail(picked || null)
        setOpenSnackbar({
          open: true,
          message: "Get payslip successfully",
          severity: "success",
        })
      } catch (e) {
        console.log(e)
        setOpenSnackbar({
          open: true,
          message: e?.data?.data || "Failed to get payslip",
          severity: "error",
        })
        setPayslipDetail(null)
      }
    })()
  }, [
    isOpen,
    data?.empCode,
    data?.currentMonth,
    data?.month,
    data?.year,
    getListPaySlip,
  ])

  useEffect(() => {
    if (!isOpen) {
      setPayslipDetail(null)
    }
  }, [isOpen])

  const handleDownloadPdf = async () => {
    if (!contentRef.current || isDownloading) return
    setIsDownloading(true)

    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
      })

      const pdf = new jsPDF("p", "mm", "a4")
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      const canvasWidth = canvas.width
      const canvasHeight = canvas.height

      const pxPerMm = canvasWidth / pdfWidth
      const pageHeightPx = Math.floor(pdfHeight * pxPerMm)

      let renderedHeight = 0
      let pageIndex = 0

      while (renderedHeight < canvasHeight) {
        const pageCanvas = document.createElement("canvas")
        const pageCtx = pageCanvas.getContext("2d")

        pageCanvas.width = canvasWidth
        pageCanvas.height = Math.min(
          pageHeightPx,
          canvasHeight - renderedHeight
        )

        pageCtx.drawImage(
          canvas,
          0,
          renderedHeight,
          canvasWidth,
          pageCanvas.height,
          0,
          0,
          canvasWidth,
          pageCanvas.height
        )

        const imgData = pageCanvas.toDataURL("image/png")
        const imgHeightMm = pageCanvas.height / pxPerMm

        if (pageIndex > 0) pdf.addPage()
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, imgHeightMm)

        renderedHeight += pageHeightPx
        pageIndex += 1
      }

      const fileNameSafe = String(monthLabel)
        .toLowerCase()
        .replace(/\s+/g, "-")
        .replace(/[^a-z0-9-]/g, "")

      pdf.save(`payslip-${fileNameSafe || "detail"}.pdf`)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Modal open={isOpen} onClose={onClose}>
      <div className="modal-leave-application-container modal-payslip-container">
        <div className="modal-leave-application-container-wrapper">
          <div ref={contentRef}>
            <MDBox className="modal-leave-application-header">
              <MDBox>
                <MDTypography
                  className="modal-leave-application-title"
                  variant="h5"
                >
                  Payslip - {monthLabel}
                </MDTypography>
              </MDBox>

              <IconButton
                onClick={onClose}
                size="small"
                data-html2canvas-ignore="true"
              >
                <CloseIcon />
              </IconButton>
            </MDBox>

            <MDBox className="modal-payslip-detail-header">
              {isLoading ? (
                <MDBox sx={{ display: "flex", alignItems: "center", gap: 2, p: 1 }}>
                  <Skeleton variant="circular" width={74} height={74} />
                  <MDBox flex={1}>
                    <Skeleton variant="text" width="55%" height={28} />
                    <Skeleton variant="text" width="40%" height={20} />
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox sx={(theme) => headerStyle(theme)}>
                  <MDBox sx={(theme) => containerStyle(theme)}>
                    <MDBox
                      component="img"
                      width={74}
                      height={74}
                      src={uiModel?.avatarUrl}
                      alt={uiModel?.employeeName || "avatar"}
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                    <MDBox>
                      <MDTypography sx={(theme) => nameStyle(theme)}>
                        {uiModel?.employeeName || data?.empCode || "-"}
                      </MDTypography>
                      <MDTypography sx={(theme) => roleStyle(theme)}>
                        {uiModel?.departmentName || "-"}{" "}
                        {uiModel?.empCode && uiModel?.empCode !== "-"
                          ? `(${uiModel.empCode})`
                          : ""}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>

            <MDBox className="modal-payslip-detail-header" mt={2} p={2}>
              {isLoading ? (
                <PayslipSkeletonContent />
              ) : (
                <>
                  <MDTypography className="modal-payslip-detail--title">
                    Earnings
                  </MDTypography>

                  <MDBox mt={2}>
                    {uiModel?.earningsSections?.length ? (
                      uiModel.earningsSections.map((section, idx) => (
                        <MDBox key={`earning-section-${idx}`} mt={idx ? 2 : 0}>
                          <MDTypography className="modal-payslip-detail--section-title">
                            {section.title}
                          </MDTypography>
                          <MDBox mt={1}>
                            {section.items.map((item, itemIdx) => (
                              <MDBox
                                className="modal-payslip-detail--sub-item"
                                key={`earning-${idx}-${itemIdx}`}
                              >
                                <MDTypography className="modal-payslip-detail--sub-item-title">
                                  {item.title}
                                </MDTypography>
                                <MDTypography className="modal-payslip-detail--sub-item-value">
                                  {item.isNumber
                                    ? formatVND(item.amount)
                                    : item.rawValue || "-"}
                                </MDTypography>
                              </MDBox>
                            ))}
                          </MDBox>
                        </MDBox>
                      ))
                    ) : (
                      <MDTypography>No earnings data</MDTypography>
                    )}
                  </MDBox>

                  <Divider width="100%" />

                  <MDTypography className="modal-payslip-detail--title" mt={2}>
                    Deductions
                  </MDTypography>

                  <MDBox mt={2}>
                    {uiModel?.deductionsSections?.length ? (
                      uiModel.deductionsSections.map((section, idx) => (
                        <MDBox key={`deduction-section-${idx}`} mt={idx ? 2 : 0}>
                          <MDTypography className="modal-payslip-detail--section-title">
                            {section.title}
                          </MDTypography>
                          <MDBox mt={1}>
                            {section.items.map((item, itemIdx) => (
                              <MDBox
                                className="modal-payslip-detail--sub-item"
                                key={`deduction-${idx}-${itemIdx}`}
                              >
                                <MDTypography className="modal-payslip-detail--sub-item-title">
                                  {item.title}
                                </MDTypography>
                                <MDTypography className="modal-payslip-detail--sub-item-value">
                                  {item.isNumber
                                    ? formatVND(item.amount)
                                    : item.rawValue || "-"}
                                </MDTypography>
                              </MDBox>
                            ))}
                          </MDBox>
                        </MDBox>
                      ))
                    ) : (
                      <MDTypography>No deductions data</MDTypography>
                    )}
                  </MDBox>

                  <Divider />

                  <MDBox mt={2} className="modal-payslip-detail--total-box">
                    <MDTypography className="modal-payslip-detail--title" mt={2}>
                      Net Pay
                    </MDTypography>
                    <MDTypography className="modal-payslip-detail--total-value">
                      {formatVND(netPay)}
                    </MDTypography>
                  </MDBox>
                </>
              )}
            </MDBox>
          </div>

          <MDBox
            className="modal-payslip-detail-header modal-payslip-detail-footer"
            mt={2}
            p={2}
            data-html2canvas-ignore="true"
          >
            <Button
              variant="outlined"
              onClick={onClose}
              className="cancel-button"
            >
              Back
            </Button>

            <Button
              className="submit-button"
              variant="contained"
              color="primary"
              onClick={handleDownloadPdf}
              disabled={isDownloading || isFetching || !uiModel}
            >
              <Download />
              &nbsp; {isDownloading ? "Generating..." : "Download PDF"}
            </Button>
          </MDBox>
        </div>
      </div>
    </Modal>
  )
}

export default ModalPaySlipDetail
